// src/render/imageOverlay.ts
// Ensure we have a top-layer, clickable overlay root that isn't blocked by pointer-events:none parents.
// This stays above content (z-index ~700) but below HUD (~1000). Always append as last child for stacking.
function ensureInteractiveClickLayer(section: HTMLElement): HTMLElement {
  let div = section.querySelector('[data-role="interactive-overlays"]') as HTMLElement | null;

  if (!div) {
    div = document.createElement("div");
    div.dataset.role = "interactive-overlays";
    div.className = "absolute inset-0";
  }

  // Enforce styles every time (in case older runs set different values)
  div.style.position = "absolute";
  (div.style as any).inset = "0";
  div.style.zIndex = "900";          // above content (500) and non-interactive overlays (300), below HUD (1000)
  div.style.pointerEvents = "auto";  // allow clicks
  div.style.visibility = "visible";

  // Ensure it is the last child so it visually stacks above other overlay containers regardless of DOM creation order.
  if (div.parentElement !== section || section.lastElementChild !== div) {
    section.append(div);
  }

  return div;
}
// src/render/imageOverlay.ts

import { getBaseUrl } from "@/utils/config";
import { ensureImageOverlayContainer } from "@/utils/overlay";
import { getPositionClasses, applyZIndexAndPointer } from "@/utils/positions";
import { parseImageSpecList, type NormalizedImageSpec } from "@/utils/imageSpec";
import { applyAnimAttrs } from "@/utils/anim";
import { resolveAsset } from "@/utils/resolveAssets";

const resolveImageSrc = (baseUrl: string, raw: string) => resolveAsset("image", baseUrl, raw);

function safeHref(h?: string | null): string | null {
  if (!h) return null;
  const u = String(h).trim();
  return /^javascript:/i.test(u) ? null : u;
}

// Target computation for anchors: honor explicit target, otherwise infer sane default.
type AnchorTarget = "_blank" | "_self" | "_parent" | "_top";

function computeTarget(href?: string | null, explicit?: AnchorTarget): AnchorTarget {
  if (explicit) return explicit;
  const u = String(href || "").trim();
  if (!u) return "_self";
  // Never new-tab for in-page anchors or phone/email
  if (/^(mailto:|tel:|#)/i.test(u)) return "_self";
  // External (http/https or protocol-relative) → new tab
  if (/^(https?:)?\/\//i.test(u)) return "_blank";
  // Site-relative → same tab
  if (u.startsWith("/")) return "_self";
  // Fallback: treat unknown schemes/relative as external conservatively
  return "_blank";
}

function applyLinkAttrs(a: HTMLAnchorElement, href: string, explicit?: AnchorTarget) {
  a.href = href;
  const t = computeTarget(href, explicit);
  a.target = t;
  if (t === "_blank") a.rel = "noopener noreferrer";
}

// resolveSrc removed in favor of resolveImageSrc

function toCssSize(v: unknown): string | undefined {
  if (v == null) return;
  const s = String(v).trim();
  if (!s) return;
  return /^\d+(\.\d+)?$/.test(s) ? `${s}px` : s;
}

// parse "3000ms"|"3s"|3 → seconds
function toSeconds(v: unknown): number | undefined {
  if (v == null || v === "") return;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const s = String(v).trim();
  if (!s) return;
  const ms = s.match(/^([0-9]*\.?[0-9]+)\s*ms$/i);
  if (ms) return parseFloat(ms[1]) / 1000;
  const sec = s.match(/^([0-9]*\.?[0-9]+)\s*s(ec)?$/i);
  if (sec) return parseFloat(sec[1]);
  const num = Number(s);
  return Number.isFinite(num) ? num : undefined;
}

// WHY: some front-matter keep width/height only in the raw token; bridge it

function sniffDimsFromRaw(raw: unknown): { width?: string | number; height?: string | number } {
  const out: { width?: string | number; height?: string | number } = {};
  if (!raw) return out;
  if (typeof raw === "string") {
    const w = raw.match(/\bwidth\s*:\s*([0-9.]+(?:px|%|rem|em|vh|vw)?)\b/i);
    const h = raw.match(/\bheight\s*:\s*([0-9.]+(?:px|%|rem|em|vh|vw)?)\b/i);
    if (w?.[1]) out.width = w[1];
    if (h?.[1]) out.height = h[1];
    return out;
  }
  if (typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    if (o.width != null) out.width = o.width as any;
    if (o.height != null) out.height = o.height as any;
    return out;
  }
  return out;
}

function normalizePosToken(p?: string): string | undefined {
  const v = String(p || "").toLowerCase().trim();
  if (!v) return undefined;
  if (v === "top") return "top-center";
  if (v === "bottom") return "bottom-center";
  if (v === "left") return "center-left";
  if (v === "right") return "center-right";
  return v;
}

function sniffTimingAndPosFromRaw(raw: unknown): { delaySec?: number; durSec?: number; position?: string } {
  if (typeof raw !== "string") return {};
  const s = raw;
  const dly = s.match(/\b(?:delay|anim-delay)\s*:\s*([0-9.]+(?:ms|s)?)\b/i)?.[1];
  const dur = s.match(/\b(?:dur|anim-dur|duration)\s*:\s*([0-9.]+(?:ms|s)?)\b/i)?.[1];
  // Support keyword tokens (e.g., top-left) OR numeric pairs like "50% 20%" / "120px 30%"
  const posKeyword = s.match(/\bposition\s*:\s*([a-z-]+)\b/i)?.[1];
  const posPair = s.match(/\bposition\s*:\s*([0-9.]+(?:px|%)?)\s+([0-9.]+(?:px|%)?)/i);
  const pos = posKeyword || (posPair ? `${posPair[1]} ${posPair[2]}` : undefined);
  return {
    delaySec: dly ? toSeconds(dly) : undefined,
    durSec:   dur ? toSeconds(dur) : undefined,
    position: pos ? normalizePosToken(pos) : undefined,
  };
}

// Heal "shattered" numbered image specs (e.g., image1:) by pulling any
// top-level attrs like width, position, anim, delay, dur, etc. back into the brace body.
function healShatteredNumberedImageSpec(attrs: Record<string, any>, key: string): void {
  if (!attrs || typeof attrs[key] !== "string") return;

  const stripOuterQuotes = (s: string) => s.replace(/^['"]|['"]$/g, "");
  const raw = stripOuterQuotes(String(attrs[key]).trim());
  if (!raw) return;

  // Extract filename and existing brace body if present.
  let filename = raw;
  let body = "";
  const m = raw.match(/^\s*([^{}]+?)\s*\{([^}]*)\}\s*$/);
  if (m) {
    filename = m[1].trim();
    body = m[2].trim();
  }

  // Known keys that may have been lifted out by the markdown/front-matter splitter.
  // Include a few synonyms and map them to canonical names used by parseInlineAttrs.
  const KNOWN: Array<[string, string]> = [
    ["width", "width"],
    ["height", "height"],
    ["position", "position"],
    ["pos", "position"],
    ["anim", "anim"],
    ["animation", "anim"],
    ["delay", "delay"],
    ["anim-delay", "delay"],
    ["dur", "dur"],
    ["anim-dur", "dur"],
    ["dist", "dist"],
    ["z", "z"],
    ["pointer", "pointer"],
    ["href", "href"],
    ["link", "href"],   // alias accepted for authoring convenience
    ["target", "target"],
    ["alt", "alt"],
    ["class", "class"],
  ];

  const extras: string[] = [];
  for (const [k, canon] of KNOWN) {
    if (attrs[k] == null) continue;
    let v = attrs[k];
    if (typeof v === "string") v = stripOuterQuotes(v.trim());
    extras.push(`${canon}: ${v}`);
    delete attrs[k]; // move it into the brace body
  }

  // If nothing to add and we already had a brace body, keep as-is.
  if (!extras.length) {
    // If we had no brace body, still normalize filename by trimming quotes.
    attrs[key] = body ? `${filename} { ${body} }` : filename;
    return;
  }

  const combined = [body, extras.join(", ")].filter(Boolean).join(", ").trim();
  attrs[key] = `${filename} { ${combined} }`;
}

// Force positional styles regardless of default classes
function applyPositionStyles(el: HTMLElement, pos: string | undefined) {
  // Explicitly set absolute positioning on the wrapper element
  el.style.position = "absolute";
  const p = String(pos || "").toLowerCase();
  // clear any side pins that may come from classes
  el.style.top = "";
  el.style.right = "";
  el.style.bottom = "";
  el.style.left = "";
  el.style.transform = "";
  // handle common positions
  switch (p) {
    case "top-left":
      el.style.top = "0";
      el.style.left = "0";
      break;
    case "top-right":
      el.style.top = "0";
      el.style.right = "0";
      break;
    case "bottom-left":
      el.style.bottom = "0";
      el.style.left = "0";
      break;
    case "bottom-right":
    case "":
      el.style.bottom = "0";
      el.style.right = "0";
      break;
    case "center":
    case "middle":
    case "center-center":
      el.style.top = "50%";
      el.style.left = "50%";
      el.style.transform = "translate(-50%, -50%)";
      break;
    case "center-left":
    case "left-center":
      el.style.top = "50%";
      el.style.left = "0";
      el.style.transform = "translateY(-50%)";
      break;
    case "center-right":
    case "right-center":
      el.style.top = "50%";
      el.style.right = "0";
      el.style.transform = "translateY(-50%)";
      break;
    case "top-center":
    case "center-top":
      el.style.top = "0";
      el.style.left = "50%";
      el.style.transform = "translateX(-50%)";
      break;
    case "bottom-center":
    case "center-bottom":
      el.style.bottom = "0";
      el.style.left = "50%";
      el.style.transform = "translateX(-50%)";
      break;
    case "top":
      el.style.top = "0";
      el.style.left = "50%";
      el.style.transform = "translateX(-50%)";
      break;
    case "bottom":
      el.style.bottom = "0";
      el.style.left = "50%";
      el.style.transform = "translateX(-50%)";
      break;
    case "left":
      el.style.top = "50%";
      el.style.left = "0";
      el.style.transform = "translateY(-50%)";
      break;
    case "right":
      el.style.top = "50%";
      el.style.right = "0";
      el.style.transform = "translateY(-50%)";
      break;
    default:
      // fallback to bottom-right if unknown
      el.style.bottom = "0";
      el.style.right = "0";
      break;
  }
}


function buildOverlayElement(
  spec: NormalizedImageSpec,
  baseUrl: string,
  fallbackDelaySec: number
): HTMLElement | null {
  const src = resolveImageSrc(baseUrl, spec.src || "");
  if (!src) return null;

  const img = document.createElement("img");
  img.decoding = "async";
  (img as any).fetchPriority = "low";
  img.loading = "lazy";
  img.alt =
    (spec.alt ?? "").replace(/^['"]|['"]$/g, "") ||
    (src.split("/").pop()?.split(".")[0] || "overlay");
  img.setAttribute("role", "presentation");
  img.classList.add("sc-anim");
  img.src = src;

  // sizing
  const cssW = toCssSize(spec.width);
  const cssH = toCssSize(spec.height);
  if (cssW) {
    img.style.setProperty("width", cssW);
    img.style.setProperty("max-width", "none"); // why: avoid Tailwind max-w-full clamp
    img.style.setProperty("height", cssH || "auto");
  } else if (cssH) {
    img.style.setProperty("height", cssH);
    img.style.setProperty("width", "auto");
    img.style.setProperty("max-width", "none");
  } else {
    img.style.setProperty("width", "clamp(96px, 28vw, 220px)");
    img.style.setProperty("height", "auto");
    img.style.setProperty("max-width", "none");
  }

  // animation (dur/delay honored like inline)
  applyAnimAttrs(img, {
    anim: spec.anim || "fade-in",
    delaySec: (spec.delaySec ?? fallbackDelaySec),
    durSec: spec.durSec,
    distPx: spec.distPx,
  });

  const position = spec.position || "bottom-right";
  const linkOrHref = (spec as any).href ?? (spec as any).link;
  const wrapper = (linkOrHref ? document.createElement("a") : document.createElement("div")) as HTMLElement;

  // x/y are already parsed at the spec layer (imageSpec.ts)
  const x = spec.x;
  const y = spec.y;

  // If precise coordinates are provided, use them and skip token-based classes.
  const hasPrecise = !!(x || y || spec.dx || spec.dy);
  if (hasPrecise) {
    // Ensure absolute positioning without token classes that pin sides.
    wrapper.className = "section-image absolute p-3";

    // Clear any pins then apply authored coordinates.
    wrapper.style.top = "";
    wrapper.style.right = "";
    wrapper.style.bottom = "";
    wrapper.style.left = "";
    wrapper.style.transform = "";

    if (x) wrapper.style.left = String(x);
    if (y) wrapper.style.top  = String(y);

    // Build transform from anchor (if any) + optional offsets (dx/dy).
    const transforms: string[] = [];

    // Honor keyword anchors if author supplied one (e.g., "center-left")
    const posTok = String(position).toLowerCase();
    switch (posTok) {
      case "center":
      case "middle":
      case "center-center":
        transforms.push("translate(-50%, -50%)");
        break;
      case "center-left":
      case "left-center":
        transforms.push("translateY(-50%)");
        break;
      case "center-right":
      case "right-center":
        transforms.push("translateY(-50%)");
        break;
      case "top-center":
      case "center-top":
        transforms.push("translateX(-50%)");
        break;
      case "bottom-center":
      case "center-bottom":
        transforms.push("translateX(-50%)");
        break;
      // other tokens ignored for precise mode
    }

    // If no explicit keyword anchor was set, center on percentage axes.
    if (!transforms.length) {
      const tx = x && /%$/.test(String(x)) ? "-50%" : "0";
      const ty = y && /%$/.test(String(y)) ? "-50%" : "0";
      if (tx !== "0" || ty !== "0") transforms.push(`translate(${tx}, ${ty})`);
    }

    // Optional offsets (dx/dy)
    if (spec.dx || spec.dy) {
      const dx = String(spec.dx || "0");
      const dy = String(spec.dy || "0");
      transforms.push(`translate(${dx}, ${dy})`);
    }

    if (transforms.length) {
      // Keep any pre-existing rotate/scale if we ever add those upstream
      const existing = wrapper.style.transform || "";
      const withoutTranslate = existing.replace(/translate\([^)]+\)\s*/,'').trim();
      wrapper.style.transform = `${transforms.join(" ")}${withoutTranslate ? " " + withoutTranslate : ""}`;
    }
  } else {
    wrapper.className = ["section-image", ...getPositionClasses(position)].join(" ");
    // Force positional styles to avoid class-based defaults overriding requested placement
    applyPositionStyles(wrapper, position);
  }
  if ((window as any).__DEBUG_IMAGE_OVERLAYS__) {
    // eslint-disable-next-line no-console
    console.debug("[imageOverlay] applyPositionStyles →", position, {
      top: wrapper.style.top, right: wrapper.style.right,
      bottom: wrapper.style.bottom, left: wrapper.style.left,
      transform: wrapper.style.transform
    });
  }
  // Always skip wrapper animation; only the IMG animates.
  // This prevents the <a> wrapper from inheriting any hidden/opacity=0 defaults
  // when animation starts, which could otherwise keep linked overlays invisible.
  wrapper.setAttribute("data-anim-skip", "1");
  wrapper.setAttribute("data-animated", "true");
  // Mark wrapper as visually "already animated" so any global [data-animated="false"] rules don't hide it.
  // Be explicit about visibility in case a theme hides [data-animated="false"] wrappers.
  (wrapper as HTMLElement).style.visibility = "visible";
  (wrapper as HTMLElement).style.opacity = "1";

  const pointer =
    spec.pointer === "auto" || (linkOrHref && spec.pointer !== "none") ? "auto" : "none";
  applyZIndexAndPointer(wrapper, spec.z, pointer);

  if (spec.class) {
    String(spec.class)
      .trim()
      .split(/\s+/)
      .map((c) => c.replace(/^['"]|['"]$/g, ""))
      .filter(Boolean)
      .forEach((c) => img.classList.add(c));
  }

  if (linkOrHref) {
    const a = wrapper as HTMLAnchorElement;
    const safe = safeHref(String(linkOrHref));
    if (safe) {
      applyLinkAttrs(a, safe, spec.target as any);
      a.classList.add("cursor-pointer");
      a.style.pointerEvents = "auto";
      const aria = img.alt && img.alt.trim() !== "" ? img.alt : (src.split("/").pop()?.split(".")[0] || "image");
      a.setAttribute("aria-label", aria);
    } else {
      const div = document.createElement("div");
      div.className = (wrapper as HTMLElement).className;
      div.setAttribute("data-anim-skip", "1");
      applyZIndexAndPointer(div, spec.z, "none");
      div.appendChild(img);
      return div;
    }
  }

  wrapper.appendChild(img);
  return wrapper;
}

export function renderHeaderImages(
  section: HTMLElement,
  attrs: Record<string, any>,
  defaultDelaySec: number = 0
): void {
  if (!section || !attrs) return;

  // NOTE: this renderer now ONLY accepts numbered keys: image1:, image2:, ...
  const list: any[] = [];
  const push = (v: any) => {
    if (v == null || v === false) return;
    if (Array.isArray(v)) list.push(...v);
    else list.push(v);
  };
  
  // Warn if legacy keys are present (ignored by design)
  if (attrs.image || attrs.images) {
    console.warn(
      "[imageOverlay] Ignoring 'image' or 'images' keys. Use numbered keys: image1:, image2:, ..."
    );
  }
  
  const numbered = Object.keys(attrs)
    .filter((k) => /^image\d+$/i.test(k))
    .sort((a, b) => {
      const na = parseInt(a.match(/\d+/)![0], 10);
      const nb = parseInt(b.match(/\d+/)![0], 10);
      return na - nb;
    });

  // Heal any shattered inline attrs for numbered keys before parsing
  for (const key of numbered) {
    try { healShatteredNumberedImageSpec(attrs, key); } catch {}
  }

  for (const key of numbered) push(attrs[key]);

  if (!list.length) return;

  const baseUrl = getBaseUrl();
  const normalized = parseImageSpecList(list);
  const debugImages = !!(attrs["debug-images"] || attrs.debugImages || section?.dataset?.debugImages === "1");
  if (debugImages) {
    // Show raw list and normalized specs before we apply fallbacks/mapping
    console.debug("[imageOverlay] raw list →", list);
    // Also show healed numbered values from attrs
    const healed = numbered.map(k => attrs[k]);
    console.debug("[imageOverlay] healed numbered →", healed);
    console.debug("[imageOverlay] normalized (pre-map) →", normalized);
  }

  // section-level size fallbacks
  const sectionWidth  = attrs["image-width"]  ?? attrs["imageWidth"]  ?? attrs["overlay-width"]  ?? attrs["overlayWidth"];
  const sectionHeight = attrs["image-height"] ?? attrs["imageHeight"] ?? attrs["overlay-height"] ?? attrs["overlayHeight"];

  const sectionAnim = attrs.anim ?? attrs["image-anim"] ?? attrs["overlay-anim"];

  // fill gaps from raw + section attrs + dur/delay fallbacks
  const specs = normalized.map((s, i) => {
    const rawDims = sniffDimsFromRaw(list[i]);
    const rawHints = sniffTimingAndPosFromRaw(list[i]);

    const delay = (typeof s.delaySec === "number" ? s.delaySec : rawHints.delaySec) ?? (defaultDelaySec || 0);
    const dur   = (typeof s.durSec   === "number" ? s.durSec   : rawHints.durSec);

    const wantedPos = normalizePosToken(s.position) ?? rawHints.position ?? "bottom-right";

    // Precise coordinates fallback: if parse layer didn't populate s.x/s.y,
    // try to derive from raw "position: '50% 30%'" authored text.
    let xCss = s.x;
    let yCss = s.y;
    if ((!xCss && !yCss) && typeof list[i] === "string") {
      const m = String(list[i]).match(/\bposition\s*:\s*([0-9.]+(?:px|%)?)\s+([0-9.]+(?:px|%)?)/i);
      if (m) {
        xCss = m[1];
        yCss = m[2];
      }
    }

    const out = {
      ...s,
      anim: s.anim ?? sectionAnim ?? "fade-in",
      position: wantedPos,
      delaySec: delay,
      durSec: dur,
      width: (s as any).width ?? rawDims.width ?? sectionWidth,
      height: (s as any).height ?? rawDims.height ?? sectionHeight,
      x: xCss,
      y: yCss,
    } as NormalizedImageSpec;
    if (debugImages) {
      // eslint-disable-next-line no-console
      console.debug("[imageOverlay] mapped spec →", out);
      // eslint-disable-next-line no-console
      console.table([{
        src: out.src,
        pos: out.position,
        anim: out.anim,
        delaySec: out.delaySec,
        durSec: out.durSec,
        width: String(out.width ?? ""),
        height: String(out.height ?? ""),
        x: String(out.x ?? ""),
        y: String(out.y ?? "")
      }]);
    }
    return out;
  });

  // de-dupe by (src|href|link|position)
  const uniq = new Map<string, NormalizedImageSpec>();
  for (const s of specs) {
    const key = `${s.src}|${((s as any).href ?? (s as any).link ?? "")}|${s.position}`;
    if (!uniq.has(key)) uniq.set(key, s);
  }
  const finalSpecs = Array.from(uniq.values());

  const nonInteractive = ensureImageOverlayContainer(section, { interactive: false });
  const interactive = ensureImageOverlayContainer(section, { interactive: true });
  const clickLayer = ensureInteractiveClickLayer(section);
  if (debugImages) {
    console.debug("[imageOverlay] containers:",
      { nonInteractive: !!nonInteractive, interactive: !!interactive,
        niChildren: nonInteractive?.children?.length ?? -1,
        iChildren: interactive?.children?.length ?? -1 });
  }
  nonInteractive.replaceChildren();
  interactive.replaceChildren();
  clickLayer.replaceChildren();

  if (debugImages) {
    // eslint-disable-next-line no-console
    console.assert(finalSpecs.length > 0, "[imageOverlay] No final specs after normalization despite a non-empty list.");
  }

  for (const s of finalSpecs) {
    try {
      const el = buildOverlayElement(s, baseUrl, defaultDelaySec);
      if (debugImages && el) {
        const img = el.querySelector?.("img") as HTMLImageElement | null;
        const styles = {
          top: (el as HTMLElement).style.top || getComputedStyle(el as HTMLElement).top,
          right: (el as HTMLElement).style.right || getComputedStyle(el as HTMLElement).right,
          bottom: (el as HTMLElement).style.bottom || getComputedStyle(el as HTMLElement).bottom,
          left: (el as HTMLElement).style.left || getComputedStyle(el as HTMLElement).left,
          transform: (el as HTMLElement).style.transform || getComputedStyle(el as HTMLElement).transform
        };
        // eslint-disable-next-line no-console
        console.debug("[imageOverlay] wrapper styles", styles, { usedX: s.x, usedY: s.y, pos: s.position });
        if (img) {
          // eslint-disable-next-line no-console
          console.debug("[imageOverlay] img data-anim", {
            anim: img.dataset.anim,
            delay: img.dataset.animDelay ?? img.dataset.delay,
            dur: img.dataset.animDur ?? img.dataset.dur
          });
        }
        // Assert that position isn't defaulted if spec asked otherwise
        const wanted = String(s.position || "").toLowerCase();
        const defaulted = (!wanted || wanted === "bottom-right") &&
                          (styles.bottom !== "0px" || styles.right !== "0px");
        // eslint-disable-next-line no-console
        console.assert(!defaulted, "[imageOverlay] Position defaulted unexpectedly", { wanted, styles });
      }
      if (!el) continue;
      if (el.tagName.toLowerCase() === "a") {
        // Append interactive links to a guaranteed clickable layer above content
        clickLayer.appendChild(el);
      } else {
        nonInteractive.appendChild(el);
      }
    } catch (e) {
      console.warn("[imageOverlay] failed to create overlay element:", s, e);
    }
  }
}